import { createModuleLayoutV2 } from "src/framework/layoutV2/createModuleLayoutV2.tsx";
import { createListV2 } from "src/framework/listV2/createListV2.tsx";
import { createEditV2 } from "src/framework/editV2/createEditV2.tsx";
import { createCreateV2 } from "src/framework/createV2/createCreateV2.tsx";
import type { Module } from "src/system/module.types.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import type { ClientConfig } from "src/client-config/getClientConfig.ts";
import { createFormLayoutRoute } from "src/framework/form/createFormLayoutRoute.tsx";
import { camelToKebabCase, mapObjectToArray } from "@exonn/utils";
import { createFormRoute } from "src/framework/form/createFormRoute.tsx";

export const buildDefaultModuleRouteObjects = ({
  module,
  clientConfig,
}: {
  module: Module;
  clientConfig: ClientConfig;
}) => {
  const resource = module.id;
  const routePaths = createModuleRoutePaths({
    resource,
  });

  const disableCreate =
    typeof module.disableCreate === "function"
      ? module.disableCreate({
          clientConfig,
        })
      : module.disableCreate;

  const disableEdit =
    typeof module.disableEdit === "function"
      ? module.disableEdit({
          clientConfig,
        })
      : module.disableEdit;

  const listRoute = {
    index: true,
    ...createListV2({
      resource,
    }),
  };

  const editRoute = getEditRoute({
    module,
    resource,
  });

  const createRoute = getCreateRoute({
    module,
    resource,
  });

  return [
    {
      path: routePaths.root,
      ...createModuleLayoutV2({
        resource,
      }),
      children: [
        module.list?.routeObject ?? listRoute,
        ...(disableEdit ? [] : [editRoute]),
        ...(disableCreate ? [] : [module.create?.routeObject ?? createRoute]),
      ],
    },
  ];
};

const getCreateRoute = ({
  module,
  resource,
}: {
  module: Module;
  resource: string;
}) => {
  if (module.create?.routeObject) {
    return module.create?.routeObject;
  }

  const routePaths = createModuleRoutePaths({
    resource,
  });
  if (module.formTabs) {
    return {
      path: routePaths.create.root,
      ...createFormLayoutRoute({
        resource: resource,
        layoutForm: module.layoutForm,
        type: "create",
      }),
      children: mapObjectToArray(module.formTabs, (tab, key, tabIndex) => {
        const index = tabIndex === 0;
        if (index) {
          return {
            index,
            path: index
              ? undefined
              : routePaths.create.tab(camelToKebabCase(key)).root,
            ...createFormRoute({
              resource: resource,
              layoutForm: module.layoutForm,
              type: "create",
            }),
          };
        }
        return {
          // index,
          path: index
            ? undefined
            : routePaths.create.tab(camelToKebabCase(key)).root,
        };
      }),
    };
  }

  return {
    path: routePaths.create.root,
    ...createCreateV2({
      resource,
    }),
  };
};

const getEditRoute = ({
  module,
  resource,
}: {
  module: Module;
  resource: string;
}) => {
  if (module.edit?.routeObject) {
    return module.edit?.routeObject;
  }

  const routePaths = createModuleRoutePaths({
    resource,
  });

  if (module.formTabs) {
    return {
      path: routePaths.edit.root,
      ...createFormLayoutRoute({
        resource: resource,
        layoutForm: module.layoutForm,
        type: "edit",
      }),
      children: mapObjectToArray(module.formTabs, (tab, key, tabIndex) => {
        const index = tabIndex === 0;
        if (index) {
          return {
            index,
            path: index
              ? undefined
              : routePaths.edit.tab(camelToKebabCase(key)).root,
            ...createFormRoute({
              resource: resource,
              layoutForm: module.layoutForm,
              type: "edit",
            }),
          };
        }

        return {
          path: index
            ? undefined
            : routePaths.edit.tab(camelToKebabCase(key)).root,
          ...(tab.routeObject
            ? tab.routeObject
            : createFormRoute({
                resource: resource,
                layoutForm: false,
                tab: camelToKebabCase(key),
                type: "edit",
              })),
        };
      }),
    };
  }

  return {
    path: routePaths.edit.root,
    ...createEditV2({
      resource,
    }),
  };
};
